// ✅ src/pages/VehicleV2/BillGenerationEntry.jsx  (UPDATED FULL FILE)
// Mount assumed:
//   app.use("/api/billGeneration", billGeneration);
//
// Backend endpoints assumed:
//   GET  /api/billGeneration/init?vehicleId=&branchId=&loginUserId=
//   POST /api/billGeneration/submit
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback structure
// - pageLoading + submitLoading states
// - cleaner init / submit / localStorage sync flow
// - consistent card / section / button styling
// - safer rights handling
// - same BillGeneration business logic preserved

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUndo,
  FaExternalLinkAlt,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/billGeneration";
const DEFAULT_ID = 0;

/* ---------------- helpers ---------------- */
const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const toInt = (v, def = DEFAULT_ID) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const safeJson = (raw, def = null) => {
  try {
    return raw ? JSON.parse(raw) : def;
  } catch {
    return def;
  }
};

const normalizeIds = (arr, def = DEFAULT_ID) => {
  if (!Array.isArray(arr)) return [];
  return arr.map((x) => toInt(x, def));
};

const defaultVehicle = {
  vehicleName: "",
  vehicleType: "",
  vehicleBasis: "",
  regNo: "",
  equipmentNo: "",
  usageCost: "0",
  fuelRate: "0",
  tdsPercent: "0",
};

const defaultParty = {
  name: "",
  pan: "",
  address: "",
};

const defaultProgress = {
  totalHrs: "0",
  totalKm: "0",
  breakingHrs: "0",
  hrRate: "0",
  kmRate: "0",
  hrAmount: "0",
  kmAmount: "0",
  breakingAmount: "0",
  driverAdvance: "0",
  bata: "0",
  progressTotal: "0",
  fuelIssued: "0",
  totalDieselCost: "0",
};

const defaultEditable = {
  particulars: "",
  advanceAmt: "0",
  shiftingAmt: "0",
  tdsPercent: "0",
  preparedBy: "",
  sparesDeduction: "0",
  materialDeduction: "0",
  maintenanceReason: "",
  maintenanceCharges: "0",
  otherCharges: "0",
  reasonForDeduction: "",
  includeDieselCost: false,
};

const BillGenerationEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const q = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const vehicleId = toStr(q.get("VehID") || q.get("id") || "");
  const vehicleOwnerType = toStr(q.get("brtype") || "O");

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const STORAGE_KEY = useMemo(
    () => `billGen_selection_${vehicleId}`,
    [vehicleId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [canSubmit, setCanSubmit] = useState(true);
  const [hasRights, setHasRights] = useState(true);

  const [billNo, setBillNo] = useState("");
  const [billDate, setBillDate] = useState("");

  const [party, setParty] = useState(defaultParty);
  const [veh, setVeh] = useState(defaultVehicle);
  const [progress, setProgress] = useState(defaultProgress);

  const [particulars, setParticulars] = useState(defaultEditable.particulars);
  const [advanceAmt, setAdvanceAmt] = useState(defaultEditable.advanceAmt);
  const [shiftingAmt, setShiftingAmt] = useState(defaultEditable.shiftingAmt);
  const [tdsPercent, setTdsPercent] = useState(defaultEditable.tdsPercent);
  const [preparedBy, setPreparedBy] = useState(defaultEditable.preparedBy);

  const [sparesDeduction, setSparesDeduction] = useState(
    defaultEditable.sparesDeduction
  );
  const [materialDeduction, setMaterialDeduction] = useState(
    defaultEditable.materialDeduction
  );
  const [maintenanceReason, setMaintenanceReason] = useState(
    defaultEditable.maintenanceReason
  );
  const [maintenanceCharges, setMaintenanceCharges] = useState(
    defaultEditable.maintenanceCharges
  );
  const [otherCharges, setOtherCharges] = useState(defaultEditable.otherCharges);
  const [reasonForDeduction, setReasonForDeduction] = useState(
    defaultEditable.reasonForDeduction
  );

  const [includeDieselCost, setIncludeDieselCost] = useState(
    defaultEditable.includeDieselCost
  );

  const [progressIds, setProgressIds] = useState([]);
  const [progressDetIds, setProgressDetIds] = useState([]);
  const [sparesIds, setSparesIds] = useState([]);
  const [sparesDetIds, setSparesDetIds] = useState([]);
  const [advanceIds, setAdvanceIds] = useState([]);
  const [shiftingIds, setShiftingIds] = useState([]);

  const isBusy = pageLoading || submitLoading || refreshing;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const readBoxClass = useMemo(
    () =>
      `w-full px-3 py-2 text-sm rounded-lg border ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-gray-100"
          : "bg-gray-50 border-gray-200 text-gray-800"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const miniButtonClass = useMemo(
    () =>
      `inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow transition-all text-white ${buttonClass} ${hoverClass}`,
    [buttonClass, hoverClass]
  );

  const summaryPillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- computed ---------------- */
  const sparesTotalAmount = useMemo(() => {
    return toNum(sparesDeduction) + toNum(materialDeduction);
  }, [sparesDeduction, materialDeduction]);

  const liveProgressTotal = useMemo(() => {
    return (
      toNum(progress.hrAmount) +
      toNum(progress.kmAmount) +
      toNum(progress.bata)
    );
  }, [progress.hrAmount, progress.kmAmount, progress.bata]);

  const baseBalanceBeforeTds = useMemo(() => {
    const vehTotal = toNum(liveProgressTotal);
    const others = toNum(otherCharges);
    const adv = toNum(advanceAmt);
    const shift = toNum(shiftingAmt);
    const maint = toNum(maintenanceCharges);
    const sparesTot = toNum(sparesTotalAmount);

    return vehTotal + others - (adv + shift + maint + sparesTot);
  }, [
    liveProgressTotal,
    otherCharges,
    advanceAmt,
    shiftingAmt,
    maintenanceCharges,
    sparesTotalAmount,
  ]);

  const tdsAmount = useMemo(() => {
    const per = toNum(tdsPercent);
    const tot = toNum(baseBalanceBeforeTds);
    if (!per || per <= 0) return 0;
    return (per * tot) / 100;
  }, [tdsPercent, baseBalanceBeforeTds]);

  const balancePayable = useMemo(() => {
    let bal = toNum(baseBalanceBeforeTds) - toNum(tdsAmount);
    if (includeDieselCost) bal += toNum(progress.totalDieselCost);
    return bal;
  }, [baseBalanceBeforeTds, tdsAmount, includeDieselCost, progress.totalDieselCost]);

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  /* ---------------- data appliers ---------------- */
  const applyVehDetailsToProgress = useCallback((vehDetails) => {
    if (!vehDetails) return;

    const totalHrs = toStr(vehDetails.totalhour ?? "0");
    const totalKm = toStr(vehDetails.totalKMHour ?? "0");
    const breakingHrs = toStr(vehDetails.breakingHrs ?? "0");
    const breakingAmount = toStr(vehDetails.breakingAmount ?? "0");
    const hrAmount = toStr(vehDetails.totalPrgAmount ?? "0");
    const kmAmount = toStr(vehDetails.totalamountkm ?? "0");
    const driverAdvance = toStr(vehDetails.advance ?? "0");
    const bata = toStr(vehDetails.bata ?? "0");
    const hrRate = toStr(vehDetails.hrrateamnt ?? "0");
    const kmRate = toStr(vehDetails.rateperkmH ?? "0");
    const fuelIssued = toStr(vehDetails.dieselQTY ?? "0");
    const totalDieselCost = toStr(vehDetails.totalDieselAmount ?? "0");

    setProgress((prev) => ({
      ...prev,
      totalHrs,
      totalKm,
      breakingHrs,
      hrRate,
      kmRate,
      hrAmount,
      kmAmount,
      breakingAmount,
      driverAdvance,
      bata,
      progressTotal: String(toNum(hrAmount) + toNum(kmAmount) + toNum(bata)),
      fuelIssued,
      totalDieselCost,
    }));

    sessionStorage.setItem(
      "billGeneration_selectedVehDetails",
      JSON.stringify(vehDetails)
    );
  }, []);

  const applyIdsFromSelectionObject = useCallback((sel) => {
    if (!sel) return;

    const pIds = normalizeIds(sel.progressIds, DEFAULT_ID);
    const pDet = normalizeIds(sel.progressDetIds, DEFAULT_ID);
    const fixedPDet =
      pIds.length && (!pDet.length || pDet.length !== pIds.length)
        ? Array.from({ length: pIds.length }, () => DEFAULT_ID)
        : pDet;

    if (pIds.length) {
      setProgressIds(pIds);
      setProgressDetIds(fixedPDet);
    }

    const sIds = normalizeIds(sel.sparesIds, DEFAULT_ID);
    const sDet = normalizeIds(sel.sparesDetIds, DEFAULT_ID);
    const fixedSDet =
      sIds.length && (!sDet.length || sDet.length !== sIds.length)
        ? Array.from({ length: sIds.length }, () => DEFAULT_ID)
        : sDet;

    if (sIds.length) {
      setSparesIds(sIds);
      setSparesDetIds(fixedSDet);
    }

    const aIds = normalizeIds(sel.advanceIds, DEFAULT_ID);
    const shIds = normalizeIds(sel.shiftingIds, DEFAULT_ID);

    if (aIds.length) setAdvanceIds(aIds);
    if (shIds.length) setShiftingIds(shIds);

    if (sel.advanceAmt != null) setAdvanceAmt(toStr(sel.advanceAmt));
    if (sel.shiftingAmt != null) setShiftingAmt(toStr(sel.shiftingAmt));
    if (sel.sparesDeduction != null) setSparesDeduction(toStr(sel.sparesDeduction));
  }, []);

  const syncFromLocalStorage = useCallback(() => {
    if (!vehicleId) return;
    const sel = safeJson(localStorage.getItem(STORAGE_KEY), null);
    applyIdsFromSelectionObject(sel);
  }, [vehicleId, STORAGE_KEY, applyIdsFromSelectionObject]);

  /* ---------------- API ---------------- */
  const loadInit = useCallback(async () => {
    if (!canUse(vehicleId, "Missing vehicleId in URL")) return;
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      setBillNo(toStr(data.billNo));
      setBillDate(toStr(data.billDate));

      setParty({
        name: toStr(data?.party?.name),
        pan: toStr(data?.party?.pan),
        address: toStr(data?.party?.address),
      });

      const v = data?.vehicle || {};
      setVeh({
        vehicleName: toStr(v.vehicleName),
        vehicleType: toStr(v.vehicleType),
        vehicleBasis: toStr(v.vehicleBasis),
        regNo: toStr(v.regNo),
        equipmentNo: toStr(v.equipmentNo),
        usageCost: toStr(v.usageCost ?? "0"),
        fuelRate: toStr(v.fuelRate ?? "0"),
        tdsPercent: toStr(v.tdsPercent ?? "0"),
      });

      setTdsPercent(toStr(v.tdsPercent ?? data?.tdsPercent ?? "0"));

      const hasSelectedTotals =
        !!location.state?.vehDetails ||
        !!sessionStorage.getItem("billGeneration_selectedVehDetails");

      if (!hasSelectedTotals) {
        const p = data?.progress || {};
        setProgress({
          totalHrs: toStr(p.totalHrs ?? "0"),
          totalKm: toStr(p.totalKm ?? "0"),
          breakingHrs: toStr(p.breakingHrs ?? "0"),
          hrRate: toStr(p.hrRate ?? "0"),
          kmRate: toStr(p.kmRate ?? "0"),
          hrAmount: toStr(p.hrAmount ?? "0"),
          kmAmount: toStr(p.kmAmount ?? "0"),
          breakingAmount: toStr(p.breakingAmount ?? "0"),
          driverAdvance: toStr(p.driverAdvance ?? "0"),
          bata: toStr(p.bata ?? "0"),
          progressTotal: toStr(p.progressTotal ?? "0"),
          fuelIssued: toStr(p.fuelIssued ?? "0"),
          totalDieselCost: toStr(p.totalDieselCost ?? "0"),
        });
      }

      const s = data?.spares || {};
      setSparesDeduction(toStr(s.sparesDeduction ?? "0"));

      setParticulars(toStr(data?.particulars ?? ""));
      setAdvanceAmt(toStr(data?.advanceAmt ?? "0"));
      setShiftingAmt(toStr(data?.shiftingAmt ?? "0"));
      setPreparedBy(toStr(data?.preparedBy ?? ""));
      setMaintenanceReason(toStr(data?.maintenanceReason ?? ""));
      setMaintenanceCharges(toStr(data?.maintenanceCharges ?? "0"));
      setOtherCharges(toStr(data?.otherCharges ?? "0"));
      setReasonForDeduction(toStr(data?.reasonForDeduction ?? ""));
      setMaterialDeduction(toStr(data?.materialDeduction ?? "0"));
      setIncludeDieselCost(Boolean(data?.includeDieselCost));

      if (data?.rights) {
        setHasRights(data.rights.canAccess !== false);
        setCanSubmit(data.rights.canAdd !== false);
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load details"
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    vehicleId,
    loginUserId,
    branchId,
    commonHeaders,
    canUse,
    location.state,
  ]);

  /* ---------------- handlers ---------------- */
  const openChild = useCallback((path) => {
    window.open(path, "_blank");
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      syncFromLocalStorage();

      const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
      if (saved) {
        try {
          applyVehDetailsToProgress(JSON.parse(saved));
        } catch {
          // ignore
        }
      }

      await loadInit();
      toast.success("Refreshed successfully");
    } catch (err) {
      toast.error(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [syncFromLocalStorage, applyVehDetailsToProgress, loadInit]);

  const handleReset = useCallback(() => {
    setParticulars(defaultEditable.particulars);
    setAdvanceAmt(defaultEditable.advanceAmt);
    setShiftingAmt(defaultEditable.shiftingAmt);
    setMaterialDeduction(defaultEditable.materialDeduction);
    setMaintenanceReason(defaultEditable.maintenanceReason);
    setMaintenanceCharges(defaultEditable.maintenanceCharges);
    setOtherCharges(defaultEditable.otherCharges);
    setReasonForDeduction(defaultEditable.reasonForDeduction);
    setPreparedBy(defaultEditable.preparedBy);
    setIncludeDieselCost(defaultEditable.includeDieselCost);

    setProgress((prev) => ({
      ...prev,
      ...defaultProgress,
    }));

    sessionStorage.removeItem("billGeneration_selectedVehDetails");

    setProgressIds([]);
    setProgressDetIds([]);
    setSparesIds([]);
    setSparesDetIds([]);
    setAdvanceIds([]);
    setShiftingIds([]);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }

    toast.info("Cleared editable fields");
  }, [STORAGE_KEY]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!canSubmit) {
        toast.error("You don't have rights to submit.");
        return;
      }

      syncFromLocalStorage();

      if (!progressIds.length) {
        toast.error("Select Vehicle Progress first (progressIds missing).");
        return;
      }

      setSubmitLoading(true);

      const payload = {
        vehicleId,
        branchId,
        loginUserId,

        billNo,
        billDate,

        particulars,
        advanceAmt,
        shiftingAmt,

        sparesDeduction,
        materialDeduction,
        sparesTotalAmount: String(sparesTotalAmount),

        maintenanceReason,
        maintenanceCharges,

        otherCharges,
        reasonForDeduction,

        tdsPercent,
        tdsAmount: String(tdsAmount),

        includeDieselCost,
        fuelIssued: progress.fuelIssued,
        totalDieselCost: progress.totalDieselCost,

        preparedBy,

        progress: {
          totalHrs: progress.totalHrs,
          totalKm: progress.totalKm,
          breakingHrs: progress.breakingHrs,
          hrAmount: progress.hrAmount,
          kmAmount: progress.kmAmount,
          breakingAmount: progress.breakingAmount,
          driverAdvance: progress.driverAdvance,
          bata: progress.bata,
          progressTotal: String(liveProgressTotal),
        },

        balancePayable: String(balancePayable),

        progressIds: progressIds.length ? progressIds : [DEFAULT_ID],
        progressDetIds:
          progressDetIds.length === progressIds.length
            ? progressDetIds
            : Array.from({ length: progressIds.length }, () => DEFAULT_ID),

        sparesIds: sparesIds.length ? sparesIds : [],
        sparesDetIds:
          sparesDetIds.length === sparesIds.length
            ? sparesDetIds
            : Array.from({ length: sparesIds.length }, () => DEFAULT_ID),

        advanceIds: advanceIds.length ? advanceIds : [],
        shiftingIds: shiftingIds.length ? shiftingIds : [],
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      toast.success("Bill Generated Successfully");

      sessionStorage.removeItem("billGeneration_selectedVehDetails");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }

      navigate("/vehicle/BillGenerationListing");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Submit failed"
      );
    } finally {
      setSubmitLoading(false);
    }
  }, [
    canSubmit,
    syncFromLocalStorage,
    progressIds,
    progressDetIds,
    sparesIds,
    sparesDetIds,
    advanceIds,
    shiftingIds,
    vehicleId,
    branchId,
    loginUserId,
    billNo,
    billDate,
    particulars,
    advanceAmt,
    shiftingAmt,
    sparesDeduction,
    materialDeduction,
    sparesTotalAmount,
    maintenanceReason,
    maintenanceCharges,
    otherCharges,
    reasonForDeduction,
    tdsPercent,
    tdsAmount,
    includeDieselCost,
    progress,
    preparedBy,
    liveProgressTotal,
    balancePayable,
    commonHeaders,
    STORAGE_KEY,
    navigate,
  ]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    if (!vehicleId) {
      toast.error("Missing vehicleId in URL");
      return;
    }

    loadInit();
    syncFromLocalStorage();

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        syncFromLocalStorage();
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [vehicleId, loadInit, syncFromLocalStorage, STORAGE_KEY]);

  useEffect(() => {
    if (location.state?.vehDetails) {
      applyVehDetailsToProgress(location.state.vehDetails);
    }
    applyIdsFromSelectionObject(location.state);
  }, [location.state, applyVehDetailsToProgress, applyIdsFromSelectionObject]);

  useEffect(() => {
    const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
    if (saved) {
      try {
        applyVehDetailsToProgress(JSON.parse(saved));
      } catch {
        // ignore
      }
    }
  }, [applyVehDetailsToProgress]);

  if (!hasRights) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">BILL GENERATION ENTRY</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
          <div className={cardClass}>
            You don&apos;t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  /* ---------------- UI helpers ---------------- */
  const Field = ({ label, children }) => (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );

  const ReadLabel = ({ value }) => (
    <div className={readBoxClass}>{toStr(value) || "—"}</div>
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header row */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Generation Entry
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle ID: {vehicleId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={summaryPillClass}>Bill No: {billNo || "-"}</span>
                <span className={summaryPillClass}>
                  Vehicle Mode: {vehicleOwnerType === "H" ? "Hire" : "Own"}
                </span>
                <span className={summaryPillClass}>Branch Type: {branchType || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate("/vehicle/BillGenerationListing")}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                type="button"
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                type="button"
                onClick={handleReset}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaUndo />
                Reset
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isBusy || !canSubmit}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass} ${
                  isBusy || !canSubmit ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {submitLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>

          {/* Header details */}
          <div className={sectionClass}>
            <div className="mb-5">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle / Party Details
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Bill No">
                <ReadLabel value={billNo} />
              </Field>

              <Field label="PAN Card No">
                <ReadLabel value={party.pan} />
              </Field>

              <Field label="Name of the Party">
                <ReadLabel value={party.name} />
              </Field>

              <Field label="Mailing Address">
                <ReadLabel value={party.address} />
              </Field>

              <Field label="Vehicle Name">
                <ReadLabel value={veh.vehicleName} />
              </Field>

              <Field label="Vehicle Basis">
                <ReadLabel value={veh.vehicleBasis} />
              </Field>

              <Field label="Vehicle Type">
                <ReadLabel value={veh.vehicleType} />
              </Field>

              <Field label="Vehicle (Equipment)">
                <ReadLabel value={veh.equipmentNo} />
              </Field>

              <Field label="Vehicle Reg No">
                <ReadLabel value={veh.regNo} />
              </Field>

              <Field label="Bill Date">
                <ReadLabel value={billDate} />
              </Field>
            </div>
          </div>

          {/* Add vehicle progress */}
          <div className={`${sectionClass} mt-6`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Add Vehicle Progress
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() =>
                    openChild(`/vehicle/BillAddVehicle?VehID=${encodeURIComponent(vehicleId)}&Type=4`)
                  }
                  className={miniButtonClass}
                >
                  <FaExternalLinkAlt />
                  Add Vehicle Progress
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=1`)
                  }
                  className={miniButtonClass}
                >
                  <FaExternalLinkAlt />
                  Add Advance
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=2`)
                  }
                  className={miniButtonClass}
                >
                  <FaExternalLinkAlt />
                  Add Shifting
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Total Hrs">
                <ReadLabel value={progress.totalHrs} />
              </Field>

              <Field label="Particulars">
                <textarea
                  rows={3}
                  value={particulars}
                  onChange={(e) => setParticulars(e.target.value)}
                  className={inputClass}
                  placeholder="Enter particulars"
                />
              </Field>

              <Field label="Total Km">
                <ReadLabel value={progress.totalKm} />
              </Field>

              <Field label="Breaking Hours">
                <ReadLabel value={progress.breakingHrs} />
              </Field>

              <Field label="Breaking Hours Amount">
                <ReadLabel value={progress.breakingAmount} />
              </Field>

              <Field label="Hours Total Amount">
                <ReadLabel value={progress.hrAmount} />
              </Field>

              <Field label="Km Total Amount">
                <ReadLabel value={progress.kmAmount} />
              </Field>

              <Field label="Driver Advance">
                <ReadLabel value={progress.driverAdvance} />
              </Field>

              <Field label="Bata (Others)">
                <input
                  type="text"
                  value={progress.bata}
                  onChange={(e) =>
                    setProgress((p) => ({
                      ...p,
                      bata: e.target.value,
                      progressTotal: String(
                        toNum(p.hrAmount) + toNum(p.kmAmount) + toNum(e.target.value)
                      ),
                    }))
                  }
                  className={inputClass}
                  placeholder="0"
                />
              </Field>

              <Field label="TDS Deductions (%)">
                <input
                  type="text"
                  value={tdsPercent}
                  onChange={(e) => setTdsPercent(e.target.value)}
                  className={inputClass}
                  placeholder="0"
                />
              </Field>

              <Field label="Total Amount">
                <ReadLabel value={liveProgressTotal.toFixed(2)} />
              </Field>

              <Field label="TDS Deductions Value">
                <ReadLabel value={tdsAmount.toFixed(2)} />
              </Field>

              <Field label="Prepared By">
                <input
                  type="text"
                  value={preparedBy}
                  onChange={(e) => setPreparedBy(e.target.value)}
                  className={inputClass}
                  placeholder="Prepared by"
                />
              </Field>
            </div>
          </div>

          {/* Add Spares */}
          <div className={`${sectionClass} mt-6`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Add Spares / Deductions
              </div>

              <button
                type="button"
                onClick={() =>
                  openChild(`/vehicle/BillAddSpares?VehID=${encodeURIComponent(vehicleId)}&Type=3`)
                }
                className={miniButtonClass}
              >
                <FaExternalLinkAlt />
                Add Spares
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Spares Deductions">
                <ReadLabel value={sparesDeduction} />
              </Field>

              <Field label="Total Amount">
                <ReadLabel value={sparesTotalAmount.toFixed(2)} />
              </Field>

              <Field label="Maintenance Reason">
                <input
                  type="text"
                  value={maintenanceReason}
                  onChange={(e) => setMaintenanceReason(e.target.value)}
                  className={inputClass}
                  placeholder="Maintenance reason"
                />
              </Field>

              <Field label="Maintenance Charges">
                <input
                  type="text"
                  value={maintenanceCharges}
                  onChange={(e) => setMaintenanceCharges(e.target.value)}
                  className={inputClass}
                  placeholder="0"
                />
              </Field>

              <Field label="Material Deductions">
                <input
                  type="text"
                  value={materialDeduction}
                  onChange={(e) => setMaterialDeduction(e.target.value)}
                  className={inputClass}
                  placeholder="0"
                />
              </Field>

              <Field label="Reason for Deduction">
                <input
                  type="text"
                  value={reasonForDeduction}
                  onChange={(e) => setReasonForDeduction(e.target.value)}
                  className={inputClass}
                  placeholder="Reason for deduction"
                />
              </Field>

              <Field label="Other Charges">
                <input
                  type="text"
                  value={otherCharges}
                  onChange={(e) => setOtherCharges(e.target.value)}
                  className={inputClass}
                  placeholder="0"
                />
              </Field>

              <Field label="Usage Cost Per Hr/KM">
                <ReadLabel value={veh.usageCost} />
              </Field>

              <Field label="Fuel Issued">
                <ReadLabel value={progress.fuelIssued} />
              </Field>

              <Field label="Fuel Cost">
                <ReadLabel value={veh.fuelRate} />
              </Field>

              <Field label="Include Diesel Cost">
                <div className="flex items-center gap-6 pt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="diesel"
                      checked={includeDieselCost === true}
                      onChange={() => setIncludeDieselCost(true)}
                    />
                    <span className={isDark ? "text-gray-200" : "text-gray-700"}>Yes</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="diesel"
                      checked={includeDieselCost === false}
                      onChange={() => setIncludeDieselCost(false)}
                    />
                    <span className={isDark ? "text-gray-200" : "text-gray-700"}>No</span>
                  </label>
                </div>
              </Field>

              <Field label="Total Diesel Cost">
                <ReadLabel value={progress.totalDieselCost} />
              </Field>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className={`text-xs uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Base Before TDS
                </div>
                <div className="text-lg font-bold mt-1">{baseBalanceBeforeTds.toFixed(2)}</div>
              </div>

              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className={`text-xs uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  TDS Amount
                </div>
                <div className="text-lg font-bold mt-1">{tdsAmount.toFixed(2)}</div>
              </div>

              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className={`text-xs uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Diesel Included
                </div>
                <div className="text-lg font-bold mt-1">{includeDieselCost ? "Yes" : "No"}</div>
              </div>

              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className={`text-xs uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Balance Payable
                </div>
                <div className="text-lg font-bold mt-1">{balancePayable.toFixed(2)}</div>
              </div>
            </div>

            <div className="mt-6 flex flex-col md:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={handleReset}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaUndo />
                Reset
              </button>

              <button
                type="button"
                onClick={() => navigate("/vehicle/BillGenerationListing")}
                disabled={isBusy}
                className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isBusy || !canSubmit}
                className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold text-white ${buttonClass} ${hoverClass} ${
                  isBusy || !canSubmit ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {submitLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading
                ? "Loading page..."
                : refreshing
                ? "Refreshing..."
                : submitLoading
                ? "Submitting..."
                : "Loading..."}
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillGenerationEntry;